/**
 * Email Verification Required Page
 * Shown when a user tries to access a gated area without verifying email
 */

import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/common/Layout';
import { Card } from '../components/common/Card';
import { Mail, AlertCircle } from 'lucide-react';

export const EmailVerificationRequiredPage: React.FC = () => {
  const { user } = useAuth();
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');

  const handleResendEmail = async () => {
    if (!user) return;

    setResending(true);
    setResendMessage('');
    setResendError('');

    try {
      const resendVerificationEmail = httpsCallable(functions, 'resendVerificationEmail');
      const result = await resendVerificationEmail({});
      const data = result.data as { success: boolean; message: string };

      if (data.success) {
        setResendMessage('Verification email sent! Please check your inbox.');
      } else {
        setResendError(data.message || 'Failed to resend email. Please try again later.');
      }
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      
      let errorMessage = 'An error occurred. Please try again later.';
      
      if (error.code === 'functions/resource-exhausted') {
        errorMessage = error.message || 'Please wait before requesting another email.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setResendError(errorMessage);
    } finally {
      setResending(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto py-12">
        <Card className="text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-amber-600" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Verification Required</h1>
          
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Please verify your email address before continuing. We've already sent you a verification link.
            </p>
          </div>

          <p className="text-gray-600 mb-4">
            A verification link was sent to <strong>{user?.email}</strong>. Click the link in the email to verify your account.
          </p>

          <p className="text-sm text-gray-500 mb-6">
            Didn't receive the email? Check your spam folder or request a new verification link.
          </p>

          <button
            onClick={handleResendEmail}
            disabled={resending}
            className="w-full px-4 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
          >
            {resending ? 'Sending...' : 'Resend Verification Email'}
          </button>

          {resendMessage && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {resendMessage}
            </div>
          )}

          {resendError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {resendError}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};
